import type { ScoutToolPart } from "./message/scoutChatMessage";

export const PROPOSE_TENANT_DOCUMENTS_TOOL_NAME = "propose_tenant_documents_for_ingestion";

export type ScoutTenantDocumentProposalDocument = {
  id: string;
  title: string;
  fileExtension: string;
  documentType: string;
  createdAt: string;
  sizeMegabytes: number | null;
};

export type ScoutTenantDocumentProposal = {
  proposalId: string;
  documents: ScoutTenantDocumentProposalDocument[];
  missingDocumentIds: string[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(value: Record<string, unknown>, key: string): string | undefined {
  const candidate = value[key];
  return typeof candidate === "string" ? candidate : undefined;
}

function readNumber(value: Record<string, unknown>, key: string): number | null {
  const candidate = value[key];
  return typeof candidate === "number" && Number.isFinite(candidate) ? candidate : null;
}

function readDocuments(value: unknown): ScoutTenantDocumentProposalDocument[] {
  if (!Array.isArray(value)) return [];
  const documents: ScoutTenantDocumentProposalDocument[] = [];
  for (const item of value) {
    if (!isRecord(item)) continue;
    const id = readString(item, "id");
    const title = readString(item, "title");
    if (!id || !title) continue;
    documents.push({
      id,
      title,
      fileExtension: readString(item, "fileExtension") ?? "",
      documentType: readString(item, "documentType") ?? "",
      createdAt: readString(item, "createdAt") ?? "",
      sizeMegabytes: readNumber(item, "sizeMegabytes"),
    });
  }
  return documents;
}

function readMissing(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === "string");
}

export function parseScoutTenantDocumentProposal(
  output: unknown
): ScoutTenantDocumentProposal | null {
  if (!isRecord(output)) return null;
  if (output.kind !== "tenant_document_ingestion_proposal") return null;
  const proposalId = readString(output, "proposalId");
  if (!proposalId) return null;
  const documents = readDocuments(output.documents);
  const missingDocumentIds = readMissing(output.missingDocumentIds);
  return { proposalId, documents, missingDocumentIds };
}

export function isScoutTenantDocumentProposalTool(toolPart: ScoutToolPart): boolean {
  return toolPart.toolName === PROPOSE_TENANT_DOCUMENTS_TOOL_NAME;
}
